import { createElement, useState } from "react";
import { Trans } from "react-i18next";
import { useDotLegalSnackbar } from "@dotlegal/dotlegal-ui-components";
import { post, put } from "@common/api/apiShared";
import { Result } from "@common/api/result";
import { useValidator } from "@common/hooks/validation";
import { useDotLegalMutation } from "@common/hooks/dotLegalReactQuery";
import { useTranslation } from "@localization/useTranslation";
import { IAddEditIncidentAffectedServiceDialogProps } from "./AddEditIncidentAffectedServiceDialog";
import { CreatedIncidentAffectedService, IncidentAffectedServiceSaveModel } from "../incidentAffectedServices/IncidentAffectedServices.types";
import { incidentAffectedServicesUrl } from "../incidentAffectedServices/IncidentAffectedServices.hooks";

export function useAddEditIncidentAffectedServiceDialogHooks(props: IAddEditIncidentAffectedServiceDialogProps) {
    const { translateString } = useTranslation();
    const snackBar = useDotLegalSnackbar();
    const incidentAffectedServiceToUpdate = props.incidentAffectedServiceToUpdate;
    const isAdd = incidentAffectedServiceToUpdate === undefined;
    const [saveModel, setSaveModel] = useState<IncidentAffectedServiceSaveModel>({ name: incidentAffectedServiceToUpdate?.name ?? props.initialName ?? "" });

    const validation = useValidator<IncidentAffectedServiceSaveModel>(
        (validators) => ({
            name: validators.validateNotEmpty((item) => item.name, translateString("name")),
        }),
        [saveModel]
    );
    validation.check(saveModel);

    const createMutation = useDotLegalMutation(createIncidentAffectedService, {
        onSuccess: (response: Result<CreatedIncidentAffectedService>) => onSaveResponse(response, "itemCreatedSuccess", () => response.value().id),
    });

    const updateMutation = useDotLegalMutation(updateIncidentAffectedService, {
        onSuccess: (response: Result<unknown>, id: string) => onSaveResponse(response, "itemEditedSuccess", () => id),
    });

    function createIncidentAffectedService() {
        return post<CreatedIncidentAffectedService>(incidentAffectedServicesUrl, saveModel);
    }

    function updateIncidentAffectedService(id: string) {
        return put<unknown>(`${incidentAffectedServicesUrl}/${id}`, saveModel);
    }

    // The backend only rejects a save because the name is taken, by a standard or a custom service.
    function onSaveResponse(response: Result<unknown>, successTranslationKey: string, getSavedIncidentAffectedServiceId: () => string) {
        if (response.success()) {
            snackBar.show(createElement(Trans, { i18nKey: successTranslationKey, values: { name: saveModel.name } }));
            props.onSaved(getSavedIncidentAffectedServiceId());
        } else {
            snackBar.show(translateString("incidentAffectedServiceExists"), "warning");
        }
    }

    const onNameChange = (name: string) => {
        setSaveModel({ name });
    };

    const onSave = () => {
        if (validation.anyHasErrors) {
            validation.setShowErrors(true);
            return;
        }

        if (incidentAffectedServiceToUpdate === undefined) {
            createMutation.mutate();
        } else {
            updateMutation.mutate(incidentAffectedServiceToUpdate.id);
        }
    };

    return {
        isAdd,
        isSaving: createMutation.isLoading || updateMutation.isLoading,
        validation,
        saveModel,
        onNameChange,
        onSave,
    };
}
